import Header from "../layout/Header";
import Footer from "../layout/Footer";
import SEO from "../SEO/SEO"

const TermsOfService = () => {
  return (
    <>
      <Header />
      <SEO
        title="Terms of Service | Nexora Machinery"
        description="Terms and conditions governing the use of Nexora Machinery services, product purchases, and business relationships."
        keywords="About Nexora Machinery, CNC Machine Supplier"
        path="/TermsofService"
      />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 text-white pt-24 pb-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
            Terms of <span className="text-cyan-400">Service</span>
          </h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto mb-6">
            Business Terms & Conditions
          </p>
          <div className="w-20 h-1 bg-cyan-400 mx-auto"></div>
        </div>
      </section>

      {/* Terms Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="prose prose-lg prose-slate max-w-none">
            <div className="bg-slate-50 rounded-xl p-6 mb-8 border-l-4 border-cyan-500">
              <p className="text-slate-600 text-lg">
                <strong>Effective Date:</strong> December 2023
                <br />
                These Terms of Service govern your use of Nexora Machinery
                services and product purchases. By engaging with our services,
                you agree to these terms.
              </p>
            </div>

            <div className="space-y-12">
              {/* Agreement */}
              <section>
                <h2 className="text-2xl font-bold text-slate-800 mb-4">
                  1. Agreement to Terms
                </h2>
                <div className="text-slate-600 space-y-4">
                  <p>
                    By accessing our website, requesting quotes, placing orders,
                    or using our services, you acknowledge that you have read,
                    understood, and agree to be bound by these Terms of Service.
                  </p>
                  <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                    <p className="text-sm font-semibold text-amber-800">
                      These terms constitute a legally binding agreement between
                      you (the "Customer") and Nexora Machinery ("Company",
                      "we", "us", or "our").
                    </p>
                  </div>
                </div>
              </section>

              {/* Products & Services */}
              <section>
                <h2 className="text-2xl font-bold text-slate-800 mb-4">
                  2. Products & Services
                </h2>
                <div className="text-slate-600 space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-700 mb-3">
                      Product Information
                    </h3>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>
                        Product specifications and descriptions are subject to
                        change without notice
                      </li>
                      <li>Images are for illustrative purposes only</li>
                      <li>Technical specifications may vary by manufacturer</li>
                      <li>
                        We reserve the right to discontinue products at any time
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-slate-700 mb-3">
                      Pricing & Quotes
                    </h3>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>All prices are in USD unless otherwise specified</li>
                      <li>
                        Quotes are valid for 30 days unless stated otherwise
                      </li>
                      <li>
                        Prices are subject to change due to market conditions
                      </li>
                      <li>
                        Additional charges may apply for custom requirements
                      </li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Ordering Process */}
              <section>
                <h2 className="text-2xl font-bold text-slate-800 mb-4">
                  3. Ordering & Payment
                </h2>
                <div className="grid md:grid-cols-2 gap-6 text-slate-600">
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h3 className="font-semibold text-slate-700 mb-3">
                      Order Acceptance
                    </h3>
                    <ul className="list-disc list-inside space-y-2 text-sm ml-2">
                      <li>Orders are subject to acceptance and availability</li>
                      <li>
                        We may require additional verification for large orders
                      </li>
                      <li>Order confirmation constitutes acceptance</li>
                      <li>
                        We may cancel orders for any reason before shipment
                      </li>
                    </ul>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h3 className="font-semibold text-slate-700 mb-3">
                      Payment Terms
                    </h3>
                    <ul className="list-disc list-inside space-y-2 text-sm ml-2">
                      <li>Payment terms: 50% advance, 50% before shipment</li>
                      <li>Net 30 available for approved business accounts</li>
                      <li>Late payments subject to 1.5% monthly interest</li>
                      <li>
                        We accept wire transfers, credit cards, and checks
                      </li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Shipping & Delivery */}
              <section>
                <h2 className="text-2xl font-bold text-slate-800 mb-4">
                  4. Shipping & Delivery
                </h2>
                <div className="text-slate-600 space-y-4">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h3 className="font-semibold text-blue-700 mb-3">
                      Delivery Terms
                    </h3>
                    <ul className="list-disc list-inside space-y-2 ml-2">
                      <li>Delivery times are estimates and not guaranteed</li>
                      <li>FOB Shipping Point unless otherwise agreed</li>
                      <li>
                        Risk of loss passes to buyer upon delivery to carrier
                      </li>
                      <li>Shipping costs are additional unless specified</li>
                    </ul>
                  </div>
                  <p className="text-sm bg-amber-50 p-4 rounded-lg border border-amber-200">
                    <strong>Note:</strong> International shipments may be
                    subject to customs duties, taxes, and import regulations
                    which are the responsibility of the buyer.
                  </p>
                </div>
              </section>

              {/* Warranties & Returns */}
              <section>
                <h2 className="text-2xl font-bold text-slate-800 mb-4">
                  5. Warranties & Returns
                </h2>
                <div className="text-slate-600 space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-700 mb-3">
                      Product Warranties
                    </h3>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>
                        Warranties are provided by the original manufacturer
                      </li>
                      <li>Warranty terms vary by product and manufacturer</li>
                      <li>
                        Warranty claims must be filed within specified periods
                      </li>
                      <li>Proof of purchase required for warranty service</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-slate-700 mb-3">
                      Return Policy
                    </h3>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>Returns require prior authorization</li>
                      <li>15% restocking fee may apply</li>
                      <li>Custom or special order items are non-returnable</li>
                      <li>
                        Products must be in original condition and packaging
                      </li>
                    </ul>
                  </div>

                  <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                    <h3 className="font-semibold text-red-700 mb-2">
                      Limitation of Liability
                    </h3>
                    <p className="text-sm text-red-600">
                      Our total liability shall not exceed the purchase price of
                      the products. We are not liable for consequential,
                      incidental, or special damages.
                    </p>
                  </div>
                </div>
              </section>

              {/* Intellectual Property */}
              <section>
                <h2 className="text-2xl font-bold text-slate-800 mb-4">
                  6. Intellectual Property
                </h2>
                <div className="text-slate-600 space-y-4">
                  <p>
                    All content on our website and marketing materials,
                    including:
                  </p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="bg-slate-50 rounded-lg p-4">
                      <h3 className="font-semibold text-slate-700 mb-2">
                        Protected Content
                      </h3>
                      <ul className="list-disc list-inside space-y-1 text-sm ml-2">
                        <li>Logos and trademarks</li>
                        <li>Product descriptions</li>
                        <li>Technical documentation</li>
                        <li>Website content and design</li>
                      </ul>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-4">
                      <h3 className="font-semibold text-slate-700 mb-2">
                        Restrictions
                      </h3>
                      <ul className="list-disc list-inside space-y-1 text-sm ml-2">
                        <li>No reproduction without permission</li>
                        <li>No commercial use of our content</li>
                        <li>No reverse engineering of products</li>
                        <li>Respect manufacturer copyrights</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              {/* Termination */}
              <section>
                <h2 className="text-2xl font-bold text-slate-800 mb-4">
                  7. Termination
                </h2>
                <div className="text-slate-600 space-y-4">
                  <p>
                    We may suspend or terminate your access to our services if:
                  </p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>You breach these terms of service</li>
                    <li>Required by law or legal process</li>
                    <li>We discontinue the services</li>
                    <li>For non-payment or fraudulent activity</li>
                  </ul>
                </div>
              </section>

              {/* Governing Law */}
              <section>
                <h2 className="text-2xl font-bold text-slate-800 mb-4">
                  8. Governing Law & Disputes
                </h2>
                <div className="text-slate-600 space-y-4">
                  <div className="bg-slate-50 rounded-xl p-6">
                    <div className="space-y-4">
                      <div>
                        <h3 className="font-semibold text-slate-700 mb-2">
                          Governing Law
                        </h3>
                        <p>
                          These terms are governed by the laws of [Your
                          State/Country] without regard to conflict of law
                          principles.
                        </p>
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-700 mb-2">
                          Dispute Resolution
                        </h3>
                        <p>
                          Parties agree to attempt mediation before pursuing
                          litigation. Any legal action shall be brought in the
                          courts of [Your City/State].
                        </p>
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-700 mb-2">
                          Force Majeure
                        </h3>
                        <p>
                          We are not liable for delays or failures due to
                          circumstances beyond our reasonable control including
                          strikes, natural disasters, or supply chain
                          disruptions.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Contact */}
              <section>
                <h2 className="text-2xl font-bold text-slate-800 mb-4">
                  9. Contact Information
                </h2>
                <div className="bg-slate-50 rounded-xl p-6 text-slate-600">
                  <p className="mb-4">
                    For questions about these Terms of Service, please contact:
                  </p>
                  <div className="space-y-2">
                    <p>
                      <strong>Nexora Machinery</strong>
                    </p>
                    <p>
                      <strong>Email:</strong> legal@nexora.com
                    </p>
                    <p>
                      <strong>Phone:</strong> +1 (555) 123-4567
                    </p>
                    <p>
                      <strong>Address:</strong> 6/11,Palarpathi,
                      Kinathukadavu,Coimbatore, Tanilnadu-642109
                    </p>
                  </div>
                </div>
              </section>

              {/* Updates */}
              <section>
                <h2 className="text-2xl font-bold text-slate-800 mb-4">
                  10. Changes to Terms
                </h2>
                <div className="text-slate-600 space-y-4">
                  <p>
                    We reserve the right to modify these terms at any time.
                    Continued use of our services after changes constitutes
                    acceptance of the modified terms.
                  </p>
                  <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                    <p className="text-sm font-semibold text-green-800">
                      We will notify customers of material changes to these
                      terms via email or website notice.
                    </p>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default TermsOfService;
